import type { ComponentType, CSSProperties } from 'react';

import type { MantineTheme } from '@mantine/core';
import { IconBarbell, IconCalendar, IconClock } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { readableTextColor } from 'utils/colors';

import type { AthleteCalendarItem } from '../types';

/** A gym can leave an event type without a colour. */
const FALLBACK_EVENT_COLOR = '#6b7280';

type IconComponent = ComponentType<{
  size?: number;
  color?: string;
  stroke?: number;
  style?: CSSProperties;
}>;

export interface CardVisuals {
  Icon: IconComponent;
  accent: string;
  /** The icon tile in Day, the surface in Week and Month. */
  tint: string;
  /** Null for workouts, which stay neutral. */
  fill: string | null;
  foreground: string;
  isWorkout: boolean;
  isCompleted: boolean;
  isHidden: boolean;
  typeLabel: string | null;
  /** The class a class-owned workout reaches the athlete through. */
  subtitle: string | null;
}

export const resolveCardVisuals = (item: AthleteCalendarItem, theme: MantineTheme): CardVisuals => {
  if (item.kind === 'workout') {
    const completed = item.isLogged;
    const statusAccent = completed ? theme.colors.green[6] : theme.colors.kiloBlue[6];
    const isClassOwned = item.source === 'kiloClassType';
    return {
      Icon: IconBarbell,
      accent: isClassOwned ? item.classTypeColor?.trim() || FALLBACK_EVENT_COLOR : statusAccent,
      tint: completed ? theme.colors.green[0] : theme.colors.kiloBlue[1],
      fill: null,
      foreground: theme.colors.gray[9],
      isWorkout: true,
      isCompleted: completed,
      isHidden: !item.isVisibleToAthletes,
      typeLabel: null,
      subtitle: isClassOwned ? item.classTypeName : null,
    };
  }

  const color = item.color?.trim() || FALLBACK_EVENT_COLOR;
  return {
    Icon: item.kind === 'appointment' ? IconClock : IconCalendar,
    accent: color,
    tint: color,
    fill: color,
    foreground: readableTextColor(color),
    isWorkout: false,
    isCompleted: false,
    isHidden: false,
    typeLabel: item.kind === 'appointment' ? 'Appointment' : 'Class Booking',
    subtitle: null,
  };
};

/** `coachName` is one comma-joined string; only the first name is shown, the rest is a count. */
export const formatCoachLabel = (coachName: string): string | null => {
  const names = coachName
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);

  if (names.length === 0) return null;
  if (names.length === 1) return `Coach ${names[0]}`;
  return `Coaches ${names[0]} +${names.length - 1}`;
};

/** `startTime` is a bare wall clock, so it needs a date to format against. */
export const formatEventTime = (startTime: string): string =>
  dayjs(`2000-01-01T${startTime}`).format('h:mm A');
